import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import {
  getCmiDigestWatchlistRunCommunities,
  getCmiDigestWatchlistRunOverview,
  getCmiDigestWatchlistRuns,
} from 'apis/CmiDigestWatchlistAPI';
import ChevronRight from 'common/Icons/ChevronRight';
import DigestSelector from 'components/CommunitiesToWatch/DigestSelector';
import { hasCmiDigestFlag } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { CMI_DIGEST_WATCHLIST_TYPE } from 'types/cmiDigestWatchlist';

import AiSummaryColumn from './AiSummaryColumn';
import DigestSectionCard from './DigestSectionCard';
import { Columns, SelectorRow } from './DigestSectionLayout';
import DigestSectionState from './DigestSectionState';
import NeedsAttentionColumn from './NeedsAttentionColumn';

const CommunitiesToWatchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 4px;
  background: linear-gradient(
    277deg,
    var(--competitor-purple),
    var(--aici-action-icon-gradient-mid) 50%,
    var(--flamingo-pink)
  );
  color: var(--white);
  font-family: var(--font-bold);
  font-size: 14px;
  padding: 10px 20px;
  cursor: pointer;
`;

const Card = styled(DigestSectionCard)`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  max-height: 552px;
  margin-bottom: 30px;
`;

const CommunitiesToWatchCard = () => {
  const navigate = useNavigate();
  const { currentUser } = useAppSelector((state) => state.auth);
  const showDigest = hasCmiDigestFlag(currentUser);
  const digestEnabled = Boolean(currentUser?.cmi_digest_preference?.enabled);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);

  const runs = useQuery({
    queryKey: ['cmi-digest-watchlist-runs'],
    queryFn: getCmiDigestWatchlistRuns,
    enabled: showDigest && digestEnabled,
    refetchOnWindowFocus: false,
  });
  const runId = digestEnabled ? (selectedRunId ?? runs.data?.[0]?.id) : undefined;

  const overview = useQuery({
    queryKey: ['cmi-digest-watchlist-overview', runId],
    queryFn: () => getCmiDigestWatchlistRunOverview(runId ?? -1),
    enabled: Boolean(runId),
    refetchOnWindowFocus: false,
  });

  const needsAttention = useQuery({
    queryKey: ['cmi-digest-watchlist-communities', runId, CMI_DIGEST_WATCHLIST_TYPE.NEEDS_ATTENTION, ''],
    queryFn: () =>
      getCmiDigestWatchlistRunCommunities(runId ?? -1, { type: CMI_DIGEST_WATCHLIST_TYPE.NEEDS_ATTENTION, search: '' }),
    enabled: Boolean(runId),
    refetchOnWindowFocus: false,
  });

  if (!showDigest) {
    return null;
  }

  const queries = [runs, overview, needsAttention];
  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);
  const showContent = !isLoading && !isError && runId && runs.data && overview.data && needsAttention.data;
  const action = runId && (
    <CommunitiesToWatchButton
      data-test-id="communities-to-watch-card-link"
      onClick={() => navigate(`/communities-to-watch?runId=${runId}`)}
    >
      <img src="/icons/ai-blob-white.svg" alt="" width={20} height={20} /> Communities to Watch
      <ChevronRight fill="var(--white)" width={20} height={20} />
    </CommunitiesToWatchButton>
  );

  return (
    <Card
      title="Communities to Watch"
      subtitle="Notable weekly happenings in your communities. Full analysis across all communities available via the button."
      action={action}
      testId="communities-to-watch-card"
    >
      <DigestSectionState
        isLoading={isLoading}
        isError={isError}
        digestEnabled={digestEnabled}
        hasDigest={Boolean(runId)}
        emptyMessage="No delivered digest has included these communities yet."
      />
      {showContent && (
        <>
          <SelectorRow>
            <DigestSelector runs={runs.data} selectedRunId={runId} onSelect={setSelectedRunId} />
          </SelectorRow>
          <Columns>
            <AiSummaryColumn lines={overview.data.overall_communities_summary_lines} />
            <NeedsAttentionColumn rows={needsAttention.data} runId={runId} />
          </Columns>
        </>
      )}
    </Card>
  );
};

export default CommunitiesToWatchCard;
